import React, { useEffect, useMemo, useState } from "react";
import { useIncrementalList } from "../hooks/useIncrementalList.js";
import { useMediaQuery } from "../hooks/useMediaQuery.js";
import { ListSentinel } from "./ListSentinel.jsx";

/**
 * Desktop list table (saas-books DataTable conventions):
 * - click a header to sort (▲/▼); sorting covers the whole list, not just the rendered chunk
 * - drag a header edge to resize (double-click resets); widths remembered per `storageKey`
 * - sticky header, accent left border on hover/selection, row click opens
 * - keyboard: ↑/↓ move between rows, Enter opens
 * - long lists render in chunks via useIncrementalList
 *
 * columns: [{ key, label, width?, align?, sortValue?(row), render?(row), noSort?, optional?, className? }]
 * `optional` columns are hidden below 1280px so dates, numbers, money and status never get squeezed.
 */
const MIN_COL_WIDTH = 60;

function readJson(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null") || fallback;
  } catch {
    return fallback;
  }
}

/** The sort the user last chose for a table ({ key, dir } or `fallback`). */
export function readStoredSort(storageKey, fallback = null) {
  return storageKey ? readJson(`books_sort_${storageKey}`, fallback) : fallback;
}

/** Rows ordered by a column's sortValue (or raw value); unsorted when `sort` is empty. */
export function sortRows(rows, columns, sort) {
  const column = sort && columns.find((c) => c.key === sort.key);
  if (!column) return rows;
  const valueOf = column.sortValue || ((row) => row[column.key]);
  const dir = sort.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" }) * dir;
  });
}

export function DataTable({
  columns: allColumns,
  rows,
  onRowClick,
  rowKey = (row) => row.id,
  selectedId = null,
  storageKey,
  defaultSort = null, // { key, dir: "asc" | "desc" }
  noun = "records",
  resetKey = ""
}) {
  const isWide = useMediaQuery("(min-width: 1280px)");
  const columns = useMemo(() => (isWide ? allColumns : allColumns.filter((c) => !c.optional)), [allColumns, isWide]);
  const widthsKey = storageKey ? `books_col_widths_${storageKey}` : null;
  const sortKey = storageKey ? `books_sort_${storageKey}` : null;
  const [widths, setWidths] = useState(() => (widthsKey ? readJson(widthsKey, {}) : {}));
  const [sort, setSort] = useState(() => readStoredSort(storageKey, defaultSort));

  useEffect(() => {
    if (widthsKey) localStorage.setItem(widthsKey, JSON.stringify(widths));
  }, [widthsKey, widths]);

  useEffect(() => {
    if (sortKey) localStorage.setItem(sortKey, JSON.stringify(sort));
  }, [sortKey, sort]);

  const sortedRows = useMemo(() => sortRows(rows, allColumns, sort), [rows, allColumns, sort]);

  const list = useIncrementalList(sortedRows, { resetKey: `${resetKey}|${sort?.key}|${sort?.dir}` });

  const toggleSort = (column) => {
    if (column.noSort) return;
    setSort((prev) => {
      if (prev?.key !== column.key) return { key: column.key, dir: "asc" };
      return { key: column.key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const startResize = (e, column) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    const startX = e.clientX;
    const startWidth = th.getBoundingClientRect().width;
    const minWidth = MIN_COL_WIDTH;
    const onMove = (ev) => {
      const next = Math.max(minWidth, Math.round(startWidth + ev.clientX - startX));
      setWidths((prev) => ({ ...prev, [column.key]: next }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.classList.remove("col-resizing");
    };
    document.body.classList.add("col-resizing");
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const resetWidth = (e, column) => {
    e.stopPropagation();
    setWidths((prev) => {
      const next = { ...prev };
      delete next[column.key];
      return next;
    });
  };

  const handleRowKeyDown = (e, row) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onRowClick?.(row);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const sibling = e.key === "ArrowDown" ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling;
    sibling?.focus();
  };

  if (!rows.length) return null;

  return (
    <>
      <div className="data-table-wrap">
        <table className={`data-table ${onRowClick ? "clickable" : ""}`}>
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={{ width: widths[column.key] || column.width || undefined }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => {
                const isSorted = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    className={`${column.align === "right" ? "align-right" : ""} ${column.noSort ? "" : "sortable"}`}
                    onClick={() => toggleSort(column)}
                    aria-sort={isSorted ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                  >
                    <span className="th-label">
                      {column.label}
                      {isSorted && <span className="sort-arrow">{sort.dir === "asc" ? "▲" : "▼"}</span>}
                    </span>
                    <span
                      className="col-resizer"
                      onMouseDown={(e) => startResize(e, column)}
                      onDoubleClick={(e) => resetWidth(e, column)}
                      onClick={(e) => e.stopPropagation()}
                      title="Drag to resize · double-click to reset"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {list.visibleItems.map((row) => {
              const id = rowKey(row);
              return (
                <tr
                  key={id}
                  tabIndex={onRowClick ? 0 : undefined}
                  className={id === selectedId ? "selected" : ""}
                  onClick={() => onRowClick?.(row)}
                  onKeyDown={(e) => handleRowKeyDown(e, row)}
                >
                  {columns.map((column) => (
                    <td key={column.key} className={`${column.align === "right" ? "align-right" : ""} ${column.className || ""}`}>
                      {column.render ? column.render(row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ListSentinel
        sentinelRef={list.sentinelRef}
        hasMore={list.hasMore}
        visibleCount={list.visibleCount}
        total={list.total}
        onShowMore={list.showMore}
        noun={noun}
      />
    </>
  );
}

export default DataTable;
